import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormControlLabel,
  Switch
} from '@mui/material';
import { Euro } from '@mui/icons-material';
import { useTeacher } from '../../contexts/TeacherContext';
import getConfig from '../../config/app-config';

const ModalPaymentTeacher = ({ open, onClose, enrollment, onPaymentUpdate }) => {
  const config = getConfig();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [paymentType, setPaymentType] = useState('cash');
  const [useCustomAmount, setUseCustomAmount] = useState(false);
  const [customAmount, setCustomAmount] = useState('');
  const [note, setNote] = useState('');
  const { addPayment } = useTeacher();

  const handleRegisterPayment = async () => {
    setLoading(true);
    setError('');
    try {
      const paymentData = {
        enrollmentId: enrollment._id,
        userId: enrollment.userId,
        amount: enrollment.amount, // Sempre l'importo originale per validazione
        paymentType: paymentType,
        customAmount: useCustomAmount && customAmount ? parseFloat(customAmount) : null,
        note: note
      };
      
      const result = await addPayment(paymentData);
      
      if (result.success) {
        setSuccess(true);
        // Aggiorna l'enrollment nel componente padre se la callback è fornita
        if (onPaymentUpdate && result.data) {
          onPaymentUpdate(result.data);
        }
      } else {
        setError(result.error || 'Errore nella registrazione pagamento');
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Errore nella registrazione pagamento');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError('');
      setSuccess(false);
      setPaymentType('cash');
      setUseCustomAmount(false);
      setCustomAmount('');
      setNote('');
      onClose();
    }
  };

  if (!enrollment) return null;

  const memberName = enrollment.user?.name || (typeof enrollment.userId === 'object' ? enrollment.userId?.name : '');
  const memberSurname = enrollment.user?.surname || (typeof enrollment.userId === 'object' ? enrollment.userId?.surname : '');

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Euro color="primary" />
        Registra Pagamento
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Pagamento registrato con successo!
          </Alert>
        )}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" gutterBottom>
            <strong>Membro:</strong> {memberName} {memberSurname}
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Importo da pagare:</strong> €{useCustomAmount && customAmount ? customAmount : enrollment.amount}
          </Typography>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="payment-type-label">Tipo di Pagamento</InputLabel>
            <Select
              labelId="payment-type-label"
              id="payment-type-select"
              value={paymentType}
              label="Tipo di Pagamento"
              onChange={(e) => setPaymentType(e.target.value)}
              disabled={loading || success}
            >
              <MenuItem value="cash">Contanti</MenuItem>
              <MenuItem value="bank_transfer">Bonifico</MenuItem>
            </Select>
          </FormControl>
          
          {config.USE_CUSTOM_AMOUNT && (
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={useCustomAmount}
                    onChange={(e) => {
                      setUseCustomAmount(e.target.checked);
                      if (!e.target.checked) {
                        setCustomAmount('');
                      }
                    }}
                    disabled={loading || success}
                  />
                }
                label="Usa importo personalizzato"
              />
              
              {useCustomAmount && (
                <TextField
                  fullWidth
                  label="Importo personalizzato (€)"
                  type="number"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  disabled={loading || success}
                  sx={{ mt: 1 }}
                  inputProps={{ min: 0, step: 0.01 }}
                  helperText={`Importo originale: €${enrollment.amount}`}
                />
              )}
            </Box>
          )}
          
          {config.WITH_PAYMENT_NOTES && (
            <TextField
              fullWidth
              label="Note (opzionale)"
              multiline
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={loading || success}
              sx={{ mt: 2 }}
              inputProps={{ maxLength: 500 }}
              helperText={`${note.length}/500 caratteri`}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {success ? 'Chiudi' : 'Annulla'}
        </Button>
        <Button
          onClick={handleRegisterPayment}
          variant="contained"
          color="primary"
          disabled={loading || success || (config.USE_CUSTOM_AMOUNT && useCustomAmount && !customAmount)}
          startIcon={loading ? <CircularProgress size={16} /> : <Euro />}
        >
          {loading ? 'Registrando...' : success ? 'Pagamento Registrato' : 'Registra Pagamento'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalPaymentTeacher; 